import { Project } from 'containers/projects/types/project'
import { ProjectThumbnail } from './Thumbnail'
import { ButtonComponent } from '@/components/commons'
import { joinWithSlash } from 'utilities/links'
import { useProfile } from 'containers/profile/hooks/useProfile'
import { User } from 'containers/user/types/user'
import { classNames } from 'react-notion'
import { Avatar } from 'containers/recommendations/Avatar'
import { Text } from '@/components/commons/typography/Text'
import { Badge } from '@mantine/core'
import { getDateFormat } from 'utilities/dates'
import { useMemo } from 'react'
import Link from 'next/link'
import { sanitizeText } from 'utilities/text'
import { useViewProjectRequest } from 'containers/projects/hooks/useViewProject'
import { Likes } from 'containers/likes'
import { SurfaceType } from 'containers/likes/types/likes'

export interface ProjectCardProps {
  project: Project
  overrideProfile?: User
}

export const ProjectCard = ({ project, overrideProfile }: ProjectCardProps) => {
  const { profile: currentProfile } = useProfile()
  const profile = overrideProfile || currentProfile

  useViewProjectRequest({
    ownerProfile: profile,
    project,
  })

  const gallery = useMemo(() => project?.gallery?.filter((image) => !!image && image !== null) || [], [project])

  const profileName = `${profile?.firstName} ${profile?.lastName}`

  const projectTitle =
    project?.title.length > 100
      ? `${project?.title?.slice(0, 100)}...`
      : project?.title

  const description =
    project?.description.length > 140
      ? `${project?.description?.slice(0, 140)}...`
      : project?.description

  const tags =
    project?.contentType?.length > 0
      ? project?.contentType
      : project?.collabInterest?.length > 0
      ? project?.collabInterest
      : []

  const paddingTopContent = useMemo(() => {
    if (gallery.length > 0) return 'pt-18'

    if (project.staffPick) return 'pt-12'

    return 'pt-18'
  }, [gallery, project.staffPick])

  const profileUrl = joinWithSlash(profile?.username?.toLowerCase())

  const projectUrl = joinWithSlash(
    profile?.username?.toLocaleLowerCase(),
    'projects',
    project?.id
  )

  if (!project) {
    console.log('Project not found')
    return null
  }

  return (
    <div
      className="bg-white-primary rounded-10 flex-1"
      style={{ overflow: 'hidden' }}
    >
      <ProjectThumbnail project={project} profile={profile} />
      <Likes surfaceType={SurfaceType.PROJECT} referencedId={project.id} />
      <div className="border-b-1 border-b-solid border-secondary full-w"></div>
      <div
        className={classNames(
          'flex flex-column gap-14 px-18 pb-18',
          paddingTopContent
        )}
      >
        <div>
          <div className="full-w flex items-center justify-space-between">
            <Link href={profileUrl} className="flex gap-10 items-center">
              <div>
                {profile?.avatar && (
                  <Avatar
                    image={profile?.avatar}
                    username={profile?.username}
                    size={25}
                  />
                )}
              </div>

              <Text
                variation="small"
                weightClass="font-bold"
                color="text-secondary"
                className="hover__text-underline"
              >
                {profileName?.length > 20
                  ? `${profileName?.slice(0, 20)}...`
                  : profileName}
              </Text>
            </Link>

            <Badge size="sm" color="#D5FBB1" className="text-secondary">
              {project?.role?.title || 'Owner'}
            </Badge>
          </div>

          <div className="mt-12">
            <Text
              weightClass="font-bold"
              color="text-secondary"
              style={{ lineHeight: 1.4 }}
            >
              {projectTitle}
            </Text>

            <Text
              variation="xSmall"
              colorClass="text-tertirary"
              className="mt-4"
            >
              <Text as="span" variation="xSmall" colorClass="text-tertirary">
                Published:{' '}
              </Text>
              {getDateFormat({
                date: project?.createdOn as number,
                short: true,
              })}
            </Text>
          </div>
        </div>

        <Text variation="small" color="text-tertirary">
          {sanitizeText(description)}
        </Text>

        {tags.length > 0 && (
          <div className="flex gap-8 wrap">
            {tags?.slice(0, 5).map((tag, index) => (
              <Badge
                key={index}
                size="sm"
                color="#FCECF2"
                className="uppercase"
                classNames={{
                  label: 'font-bold',
                }}
                styles={{
                  label: {
                    color: '#E64980',
                  },
                }}
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}

        <ButtonComponent
          size="xSmall"
          variation="dark"
          label="View Project"
          className="mt-14"
          fullWidth
          to={projectUrl}
        />
      </div>
    </div>
  )
}
